import { DateOnly } from '@microsoft/kiota-abstractions';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  FileStreamResult,
  FinancialReport,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { downloadFile } from '@/util/file-download.ts';

type FinancialReportRow = Omit<FinancialReport, 'date'> & {
  date?: string | null;
};

type FinancialReportFormData = FormData | Record<string, unknown>;

function toDateOnly(value: unknown): DateOnly | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  if (value instanceof DateOnly) {
    return value;
  }

  if (value instanceof Date) {
    return DateOnly.fromDate(value);
  }

  if (typeof value === 'string') {
    return DateOnly.parse(value) ?? null;
  }

  return undefined;
}

function toNumberOrNull(value: unknown): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
}

function toDateString(value: { toString(): string } | null | undefined): string | null {
  return value ? value.toString() : null;
}

function normalizeReport(entry: FinancialReport | undefined): FinancialReportRow | null {
  if (!entry) {
    return null;
  }

  return {
    ...entry,
    date: toDateString(entry.date),
  };
}

function normalizeReports(entries: FinancialReport[] | null | undefined): FinancialReportRow[] {
  return (entries ?? []).map(entry => ({
    ...entry,
    date: toDateString(entry.date),
  }));
}

async function getArrayBuffer(value: FormDataEntryValue | unknown): Promise<ArrayBuffer | undefined> {
  if (value instanceof Blob) {
    return await value.arrayBuffer();
  }

  return undefined;
}

async function buildCreateRequestBody(data: FinancialReportFormData) {
  if (data instanceof FormData) {
    return {
      amount: toNumberOrNull(data.get('Amount')),
      date: toDateOnly(data.get('Date')),
      formFile: await getArrayBuffer(data.get('FormFile')),
      numberOfSeats: toNumberOrNull(data.get('NumberOfSeats')),
      tourOperatorId: toNumberOrNull(data.get('TourOperatorId')),
    };
  }

  return {
    amount: toNumberOrNull(data.Amount ?? data.amount),
    date: toDateOnly(data.Date ?? data.date),
    formFile: await getArrayBuffer(data.FormFile ?? data.formFile),
    numberOfSeats: toNumberOrNull(data.NumberOfSeats ?? data.numberOfSeats),
    tourOperatorId: toNumberOrNull(data.TourOperatorId ?? data.tourOperatorId),
  };
}

export const allotmentsFinancialReportsStore = defineStore('allotmentsFinancialReports', () => {
  const apiClient = useApiClient();

  const loading = ref<boolean>(true);
  const list = ref<FinancialReportRow[]>([]);
  const file = ref<FileStreamResult | null>(null);
  const added = ref<FinancialReportRow | null>(null);

  async function get(): Promise<FinancialReportRow[]> {
    loading.value = true;

    try {
      const response = await apiClient.financialReport.get();
      list.value = normalizeReports(response);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  async function getById({ id }: { id?: number } = {}): Promise<FinancialReportRow | null> {
    if (id === undefined) {
      return null;
    }

    loading.value = true;

    try {
      const response = await apiClient.financialReport.byId(id).get();
      return normalizeReport(response);
    } finally {
      loading.value = false;
    }
  }

  async function getFile({ id }: { id?: number } = {}): Promise<FileStreamResult | undefined> {
    if (id === undefined) {
      return undefined;
    }

    const response = await apiClient.financialReport.download.byId(id).get();
    file.value = response ?? null;
    return downloadFile(response, `FinancialReport-${id}.pdf`);
  }

  async function add(data: FinancialReportFormData): Promise<FinancialReportRow | null> {
    loading.value = true;

    try {
      const response = await apiClient.financialReport.post(await buildCreateRequestBody(data));
      added.value = normalizeReport(response);
      return added.value;
    } finally {
      loading.value = false;
    }
  }

  async function deleteFinancialReport({ id = null }: { id?: number | null }) {
    loading.value = true;

    try {
      return await apiClient.financialReport.delete({
        queryParameters: {
          id: id ?? undefined,
        },
      });
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    list,
    file,
    added,
    get,
    getById,
    getFile,
    add,
    delete: deleteFinancialReport,
  };
});
